/**
 * Notifications API service (G5.5 — the in-app channel's surface).
 *
 * Backed by /api/notifications (Story 9.1), which includes tenant-wide
 * agent-loop notifications (user_id NULL) alongside the user's own.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface NotificationItem {
  id: string;
  event_type: string;
  importance: string;
  title: string;
  message: string;
  action_url: string | null;
  entity_type: string | null;
  entity_id: string | null;
  status: string;
  created_at: string;
  read_at: string | null;
}

export interface NotificationsListResponse {
  notifications: NotificationItem[];
  total: number;
  unread_count: number;
}

export async function fetchNotifications(
  limit = 20
): Promise<NotificationsListResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/notifications?limit=${limit}`,
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<NotificationsListResponse>(response);
}

export async function fetchUnreadCount(): Promise<number> {
  const response = await fetch(
    `${API_BASE_URL}/api/notifications/unread/count`,
    { method: 'GET', headers: createHeaders() }
  );
  const data = await handleResponse<{ count: number }>(response);
  return data.count;
}

export async function markNotificationRead(id: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/notifications/${id}/read`,
    { method: 'PATCH', headers: createHeaders() }
  );
  await handleResponse<{ success: boolean }>(response);
}

export async function markAllNotificationsRead(): Promise<number> {
  const response = await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
    method: 'POST',
    headers: createHeaders(),
  });
  const data = await handleResponse<{ marked_count: number }>(response);
  return data.marked_count;
}
